import {
    useAPIQuery,
    useGlobalAPIQueryContext,
} from "@dev2_techwalnut/tw-api-query-hook";
import { Table } from "@dev2_techwalnut/tw-react-crud-dashlite";
import React, { useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import Swal from "sweetalert2";
import Rejection from "./Rejection";
import moment from "moment/moment";
import Error from "../../ErrorPages/Error";
import SalesOrderPerformanceMetrix from "./SalesOrderPerformanceMetrix";
import PieChartComponent from "./charts/PieChart";
import BarChartComponent from "./charts/BarChart";
import RejectReasonModal from "./RejectReasonModal";

const SalesOrder = () => {
    const userID = secureLocalStorage.getItem("userID");
    const privileges = secureLocalStorage.getItem("privileges");

    const isAllFilterValue = (value) =>
        String(value ?? "")
            .trim()
            .toLowerCase() === "all";

    const TypeMasterQuery = useAPIQuery({
        key: "api/type-master",
        endpoint: "/api/type-master/",
    });

    const dispatched = TypeMasterQuery.useFetch({
        params: {
            q: "Dispatched",
            c: "name",
        },
    });

    const completed = TypeMasterQuery.useFetch({
        params: {
            q: "Completed",
            c: "name",
        },
    });

    const onHold = TypeMasterQuery.useFetch({
        params: {
            q: "On hold",
            c: "name",
        },
    });

    const approved = TypeMasterQuery.useFetch({
        params: {
            q: "Approved",
            c: "name",
        },
    });

    const rejected = TypeMasterQuery.useFetch({
        params: {
            q: "Rejected",
            c: "name",
        },
    });

    const TypeMasterQueryData = TypeMasterQuery.useFetch({
        params: {
            q: "Sales Order Status",
            c: "category",
        },
    });

    const columns = [
        {
            accessor: "created_at",
            header: "Created On",
            cell: (row) => (
                <>
                    {row?.created_at
                        ? moment(row?.created_at).format("DD-MM-YYYY HH:mm")
                        : "--"}
                </>
            ),
            sortable: true,
            columnWidth: 150,
        },
        {
            accessor: "created_by",
            header: "Created by",
        },
        {
            accessor: "so_number",
            header: "Sales Order number",
        },
        {
            accessor: "client",
            header: "Client Name",
            cell: (row) => (
                <>
                    {row?.client?.user?.first_name +
                        " " +
                        row?.client?.user?.last_name}
                </>
            ),
        },
        {
            accessor: "product",
            header: "Product Name",
            cell: (row) => <>{row?.product?.item_name}</>,
        },
        {
            accessor: "product",
            header: "Product ID",
            cell: (row) => <>{row?.product?.item_code}</>,
        },
        {
            accessor: "target_date",
            header: "Target Date",
            cell: (row) => <>{moment(row?.target_date).format("DD-MM-YYYY")}</>,
            sortable: true,
            columnWidth: 100,
        },
        {
            accessor: "dispatch_location",
            header: "Dispatch Location",
        },
        {
            accessor: "quantity",
            header: "Quantity",
        },
        privileges.includes("sales_order_approval") && {
            accessor: "activity_status",
            header: "Approve Sales Order",
            cell: (row) => (
                <>
                    {row?.activity_status ===
                    Number(approved.data?.results?.data?.[0]?.value) ? (
                        <span className="badge badge-dot bg-success">
                            {approved.data?.results?.data?.[0]?.name}
                        </span>
                    ) : row?.activity_status ===
                      Number(rejected.data?.results?.data?.[0]?.value) ? (
                        <span className="badge badge-dot bg-danger">
                            {rejected.data?.results?.data?.[0]?.name}
                        </span>
                    ) : row?.activity_status ===
                      Number(onHold.data?.results?.data?.[0]?.value) ? (
                        <>
                            <div className="d-flex justify-content-evenly">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() =>
                                        handleApproveSalesOrder(row, true)
                                    }
                                >
                                    <em className="icon ni ni-check"></em>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() =>
                                        handleApproveSalesOrder(row, false)
                                    }
                                >
                                    <em className="icon ni ni-cross"></em>
                                </button>
                            </div>
                        </>
                    ) : row?.activity_status ===
                      Number(completed.data?.results?.data?.[0]?.value) ? (
                        <span className="badge badge-dot bg-info">
                            {completed.data?.results?.data?.[0]?.name}
                        </span>
                    ) : row?.activity_status ===
                      Number(dispatched.data?.results?.data?.[0]?.value) ? (
                        <span className="badge badge-dot bg-success">
                            {dispatched.data?.results?.data?.[0]?.name}
                        </span>
                    ) : null}
                </>
            ),
            filterable: true,
            filterType: "dropdown",
            filterOpt: TypeMasterQueryData.data
                ? TypeMasterQueryData.data?.results?.data?.map((type) => ({
                      label: type?.name,
                      value: type?.value,
                  }))
                : [],
        },
        {
            accessor: "action_button",
            header: "Action",
            cell: (row) => {
                const approvedStatus = Number(
                    approved.data?.results?.data?.[0]?.value
                );
                const rejectedStatus = Number(
                    rejected.data?.results?.data?.[0]?.value
                );
                const completedStatus = Number(
                    completed.data?.results?.data?.[0]?.value
                );
                const dispatchedStatus = Number(
                    dispatched.data?.results?.data?.[0]?.value
                );
                const statusDataReady =
                    approved.data &&
                    rejected.data &&
                    completed.data &&
                    dispatched.data;
                const isRejected =
                    statusDataReady && row.activity_status === rejectedStatus;
                const isDispatched =
                    statusDataReady && row.activity_status === dispatchedStatus;
                const canUseDraftActions =
                    statusDataReady &&
                    row.activity_status !== approvedStatus &&
                    row.activity_status !== rejectedStatus &&
                    row.activity_status !== completedStatus &&
                    row.activity_status !== dispatchedStatus;
                const canUpdateTargetDate =
                    statusDataReady &&
                    privileges.includes("sales_order_target_date_update") &&
                    !isRejected &&
                    !isDispatched;
                const canEditSalesOrder =
                    canUseDraftActions &&
                    privileges.includes("sales_order_update");
                const canDeleteSalesOrder =
                    canUseDraftActions &&
                    privileges.includes("sales_order_delete");

                if (
                    canEditSalesOrder ||
                    canDeleteSalesOrder ||
                    canUpdateTargetDate
                ) {
                    return (
                        <div className="drodown">
                            <a
                                href="#"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <em className="icon ni ni-more-v"></em>
                            </a>
                            <div className="dropdown-menu dropdown-menu-end">
                                <ul className="link-list-plain">
                                    {canEditSalesOrder && (
                                        <li>
                                            <a
                                                className="text-warning"
                                                onClick={() =>
                                                    handleEditRow(row)
                                                }
                                                style={{ cursor: "pointer" }}
                                            >
                                                Edit
                                            </a>
                                        </li>
                                    )}
                                    {canDeleteSalesOrder && (
                                        <li>
                                            <a
                                                className="text-danger"
                                                onClick={() =>
                                                    handleDeleteRow(row)
                                                }
                                                style={{ cursor: "pointer" }}
                                            >
                                                Delete
                                            </a>
                                        </li>
                                    )}
                                    {canUpdateTargetDate && (
                                        <li>
                                            <button
                                                type="button"
                                                className="text-primary border-0 bg-transparent p-0"
                                                onClick={() =>
                                                    handleUpdateTargetDate(row)
                                                }
                                                style={{ cursor: "pointer" }}
                                            >
                                                Update Target Date
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    );
                }

                if (isRejected) {
                    return <RejectReasonModal reason={row?.comment} />;
                }

                return "No Actions";
            },
        },
    ].filter(Boolean);

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialEntries = Number(searchParams.get("entries")) || 10;
    const initialPage = Number(searchParams.get("page")) || 1;
    const initialQueries = searchParams.get("q")?.split(",") || [];
    const initialColumns = searchParams.get("c")?.split(",") || [];

    const [entries, setEntries] = useState(initialEntries);
    const [pageIndex, setPageIndex] = useState(initialPage - 1);
    const [sorting, setSorting] = useState([]);
    const [rejectRowDetails, setRejectRowDetails] = useState(null);
    const { invalidateApi } = useGlobalAPIQueryContext();
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [columnFilterKey, setColumnFilterKey] = useState("");
    const [columnFilterValue, setColumnFilterValue] = useState("");

    const { useFetch, useUpdate: updateSalesOrder } = useAPIQuery({
        key: "sales_order",
        endpoint: "/api/sales-order/",
    });

    const { useUpdate: updateSalesOrderTargetDate } = useAPIQuery({
        key: "sales_order_target_date",
        endpoint: "/api/sales-order-target-date/",
    });

    const { data, isLoading, isFetching, isError, error } = useFetch(
        {
            params: {
                entries: entries,
                page_no: pageIndex + 1,
                q:
                    globalFilterValue !== ""
                        ? globalFilterValue
                        : columnFilterValue,
                c: globalFilterValue === "" ? columnFilterKey : "",
                sort_q:
                    sorting &&
                    sorting
                        ?.map((item) => (item?.desc ? "desc" : "asc"))
                        ?.join(","),
                sort_c: sorting && sorting?.map((item) => item?.id)?.join(","),
            },
            abortOnNewRequest: true,
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 3,
            retry: 3,
            keepPlaceholder: true,
            refetchOnReconnect: true,
        },
        [sorting]
    );

    const { mutate: approveSalesOrder, isPending: updatingSalesOrder } =
        updateSalesOrder({
            onSuccess: () => {
                invalidateApi({ key: "work_order" });
            },
            successMessageFromServer: true,
            errorMessageFromServer: true,
        });

    const { mutate: updateTargetDate, isPending: updatingTargetDate } =
        updateSalesOrderTargetDate({
            onSuccess: () => {
                invalidateApi({ key: "sales_order" });
                invalidateApi({ key: "work_order" });
            },
            successMessageFromServer: true,
            errorMessageFromServer: true,
        });

    const handleApproveSalesOrder = (row, approve_status) => {
        if (approve_status) {
            Swal.fire({
                text: `Would you like to approve Sales Order?`,
                showDenyButton: true,
                confirmButtonText: "Yes",
                denyButtonText: `No, cancel!`,
            }).then((result) => {
                if (result.isConfirmed) {
                    approveSalesOrder({
                        id: row?.id,
                        data: { activity_status: 1, updated_by: userID },
                    });
                }
            });
        } else {
            Swal.fire({
                text: `Would you like to reject the Sales Order?`,
                showDenyButton: true,
                confirmButtonText: "Yes",
                denyButtonText: `No, cancel!`,
            }).then((result) => {
                if (result.isConfirmed) {
                    document.getElementById("reject-sales-order").click();
                    setRejectRowDetails(row);
                    // approveSalesOrder({
                    //   id: row?.id + "/",
                    //   data: { activity_status: 0 },
                    // });
                }
            });
        }
    };

    const handleUpdateTargetDate = (row) => {
        const currentTargetDate = moment(row?.target_date).format("YYYY-MM-DD");

        Swal.fire({
            title: "Update Target Date",
            input: "date",
            inputValue: currentTargetDate,
            inputAttributes: {
                min: currentTargetDate,
            },
            showCancelButton: true,
            confirmButtonText: "Update",
            preConfirm: (value) => {
                if (!value) {
                    Swal.showValidationMessage("Target Date is required");
                    return false;
                }

                if (moment(value).isBefore(currentTargetDate, "day")) {
                    Swal.showValidationMessage(
                        "Target Date cannot be earlier than the current target date"
                    );
                    return false;
                }

                return value;
            },
        }).then((result) => {
            if (result.isConfirmed) {
                updateTargetDate({
                    id: row?.id,
                    data: {
                        target_date: result.value,
                        updated_by: userID,
                    },
                });
            }
        });
    };

    const { useDelete } = useAPIQuery({
        key: "sales_order",
        endpoint: "/api/sales-order/",
    });

    const { mutate: deleteData } = useDelete();

    const handleGlobalFilterChange = (value) => {
        const q = value?.trim() || "";
        if (q !== "") {
            setGlobalFilterValue(q);
            setColumnFilterKey("");
            setColumnFilterValue("");
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.delete("c");
                return searchParams;
            });
        } else if (q === "") {
            setGlobalFilterValue("");
            setColumnFilterKey("activity_status");
            setColumnFilterValue("2");

            setSearchParams((params) => {
                params.set("q", "2");
                params.set("c", "activity_status");
                return params;
            });
        }
    };

    const handleColumnFilterChange = (value) => {
        const activeFilters = value
            .map((val) => ({
                id: val.id,
                value: String(val.value ?? "").trim(),
            }))
            .filter((val) => val.id && val.value && !isAllFilterValue(val.value));

        const q = activeFilters.map((val) => val.value).join(",");
        const c = activeFilters.map((val) => val.id).join(",");
        if (q !== "" && c !== "") {
            setGlobalFilterValue("");
            setColumnFilterKey(c);
            setColumnFilterValue(q);
            setSearchParams((searchParams) => {
                searchParams.set("q", q);
                searchParams.set("c", c);
                return searchParams;
            });
        } else {
            setGlobalFilterValue("");
            setColumnFilterKey("");
            setColumnFilterValue("");

            setSearchParams((searchParams) => {
                searchParams.delete("q");
                searchParams.delete("c");
                return searchParams;
            });
        }
    };

    const handlePagination = (value) => {
        setSearchParams((searchParams) => {
            searchParams.set("entries", value.pageSize);
            searchParams.set("page", value.pageIndex + 1);
            return searchParams;
        });
        setPageIndex(value.pageIndex);
        setEntries(value.pageSize);
    };

    const handleSortingChnage = (value) => {
        console.log(value);
        setSorting(value);
    };

    const handleEditRow = (row) => {
        navigate("/edit-sales-order", { state: { editpost: row } });
    };

    const handleDeleteRow = (row) => {
        const entry_id = row.id;
        Swal.fire({
            text: "Are you sure? You will not be able to recover this data!",
            showDenyButton: true,
            confirmButtonText: "Yes",
            denyButtonText: `No, cancel!`,
        }).then((result) => {
            if (result.isConfirmed) {
                deleteData({ id: entry_id });
            }
        });
    };

    if (isError) return <Error />;

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex">
                        <h3 className="nk-block-title page-title">
                            Sales Order
                        </h3>
                        {isLoading ? (
                            <div
                                className="spinner-border text-primary ms-2"
                                role="status"
                            >
                                <span className="sr-only"></span>
                            </div>
                        ) : (
                            ""
                        )}
                    </div>
                    {privileges.includes("sales_order_create") && (
                        <div className="nk-block-head-content">
                            <NavLink
                                to="/create-sales-order"
                                className="toggle btn btn-icon btn-primary d-md-none"
                                id="button-button-create-sales-order"
                            >
                                <em className="icon ni ni-plus"></em>
                            </NavLink>

                            <NavLink
                                to="/create-sales-order"
                                className="toggle btn btn-primary d-none d-md-inline-flex"
                                id="button-button-create-sales-order"
                            >
                                <em className="icon ni ni-plus"></em>
                                <span>Create Sales Order</span>
                            </NavLink>
                        </div>
                    )}
                </div>
            </div>
            <div id="accordion" className="accordion">
                <div className="accordion-item">
                    <a
                        href="#"
                        className="accordion-head"
                        data-bs-toggle="collapse"
                        data-bs-target={`#so-analytics`}
                    >
                        <h6 className="title">Analytics</h6>
                        <span className="accordion-icon"></span>
                    </a>
                    <div
                        className="accordion-body collapse"
                        id={`so-analytics`}
                        data-bs-parent="#accordion"
                    >
                        <div className="accordion-inner">
                            <h5>Key Performance Metrics</h5>
                            <SalesOrderPerformanceMetrix />
                            <div className="row g-3 mt-3">
                                <div className="col-lg-6 col-md-12">
                                    <PieChartComponent />
                                </div>
                                <div className="col-lg-6 col-md-12">
                                    <BarChartComponent />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {isLoading ? (
                <div>Loading...</div>
            ) : !isError ? (
                <Table
                    data={data ? data?.results?.data : []}
                    columns={columns}
                    pagination
                    pageIndex={pageIndex}
                    pageSize={entries}
                    rowCount={data ? data?.count : 0}
                    isLoading={
                        isFetching || updatingSalesOrder || updatingTargetDate
                    }
                    globalSearch
                    editRows={false}
                    deleteRows={false}
                    filterModal={false}
                    selectRows={false}
                    defaultGlobalSearchValue={
                        initialQueries.length > 0 && initialColumns.length <= 0
                            ? initialQueries[0]
                            : undefined
                    }
                    defaultColumnFiltersValue={
                        initialQueries.length > 0 && initialColumns.length > 0
                            ? initialColumns.map((column, index) => ({
                                  id: column,
                                  value: initialQueries?.[index] ?? "",
                              }))
                            : undefined
                    }
                    onGlobalFilterChange={handleGlobalFilterChange}
                    onColumnFilterChange={handleColumnFilterChange}
                    onPaginationChange={handlePagination}
                    onSortingChange={handleSortingChnage}
                />
            ) : null}

            <Rejection rejectRowDetails={rejectRowDetails} />

            <button
                id="reject-sales-order"
                type="button"
                data-bs-toggle="modal"
                data-bs-target="#reject_sales_order"
                style={{ display: "none" }}
            >
                Open Modal 2
            </button>
        </>
    );
};

export default SalesOrder;
